<script lang="ts">
	/**
	 * The grip a row is dragged by — and the way to reorder without a pointer at all (§14).
	 *
	 * A drag-only list is unusable with a keyboard and unusable on a screen reader, and this is an
	 * admin screen where the order being arranged is `Product.rank`: §9.3's upgrade rule, not a
	 * decoration. So the handle is a real `<button>` that moves its row with the arrow keys, and
	 * the pointer drag is the shortcut rather than the mechanism.
	 *
	 * The button is what carries `draggable`, not the whole row: a row that is draggable everywhere
	 * swallows text selection and turns every toggle and link in it into a drag start.
	 */

	import { createEventDispatcher } from 'svelte';

	/** What this handle moves, named in the label so a screen reader says which row it has. */
	export let label: string = '';
	export let disabled: boolean = false;

	const dispatcher = createEventDispatcher<{ move: { direction: -1 | 1 } }>();

	function fnKeydown(event: KeyboardEvent): void {
		if (disabled) return;
		if (event.key !== 'ArrowUp' && event.key !== 'ArrowDown') return;

		// Or the browser scrolls the page under the admin instead of moving the row they are on.
		event.preventDefault();
		dispatcher('move', { direction: event.key === 'ArrowUp' ? -1 : 1 });
	}
</script>

<button
	type="button"
	draggable={!disabled}
	{disabled}
	class="cursor-grab active:cursor-grabbing text-default hover:text-gray-900 dark:hover:text-white disabled:cursor-not-allowed disabled:opacity-50 px-1"
	aria-label={`Reorder ${label}`}
	title="Drag to reorder, or use the arrow keys"
	on:keydown={fnKeydown}
>
	<i class="fa-solid fa-grip-vertical" />
</button>
